import * as v from 'valibot';

// Import AssociatedTraveller type (already has Valibot schema in connector)

// ============================================================================
// Traveller Validation Response Types
// ============================================================================

/**
 * Traveller field error mapping schema
 * Maps field names to their error messages
 */
export const TravellerFieldErrorsSchema = v.object({
  rowFields: v.record(v.string(), v.string()),
});

export type TravellerFieldErrors = v.InferOutput<typeof TravellerFieldErrorsSchema>;

/**
 * Traveller information for airline rules
 */
export const TravellerInfoSchema = v.object({
  title: v.string(),
  paxType: v.string(),
  gender: v.string(),
  fName: v.string(),
  lName: v.string(),
  fNameHeading: v.string(),
  lNameHeading: v.string(),
});

export type TravellerInfo = v.InferOutput<typeof TravellerInfoSchema>;

/**
 * Airline rule information for name modifications
 */
export const AirlineRuleInfoSchema = v.object({
  airlines: v.array(v.string()),
  modified: v.boolean(),
  airlineMessage: v.string(),
  inputReq: v.boolean(),
  ruleId: v.string(),
  travellerInfo: TravellerInfoSchema,
  confirmationMessage: v.string(),
});

export type AirlineRuleInfo = v.InferOutput<typeof AirlineRuleInfoSchema>;

// ============================================================================
// Validation Response States (Discriminated Union)
// ============================================================================

/**
 * Complete traveller information detail
 */
export const TravellerInfoDetailSchema = v.object({
  rowFields: v.record(v.string(), v.string()),
  //   grpFields: v.optional(TravellerGrpFieldsSchema),
});

export type TravellerInfoDetail = v.InferOutput<typeof TravellerInfoDetailSchema>;

/**
 * Individual traveller for booking
 */
export const AssociatedTravellerSchema = v.object({
  travellerId: v.string(),
  infoDetail: TravellerInfoDetailSchema,
  paxType: v.string(), // "ADULT" | "CHILD" | "INFANT"
  userPrimaryTravellerId: v.number(),
  isSelected: v.boolean(),
});

export type AssociatedTraveller = v.InferOutput<typeof AssociatedTravellerSchema>;

/**
 * Success response - All validations passed
 */
export const ValidationSuccessResponseSchema = v.object({
  type: v.literal('success'),
  isValid: v.boolean(),
  traveller: AssociatedTravellerSchema,
});

export type ValidationSuccessResponse = v.InferOutput<typeof ValidationSuccessResponseSchema>;

/**
 * Error response - Validation failed
 */
export const ValidationErrorResponseSchema = v.object({
  type: v.literal('error'),
  isValid: v.boolean(),
  errors: v.optional(TravellerFieldErrorsSchema),
  errorMessage: v.optional(v.string()),
});

export type ValidationErrorResponse = v.InferOutput<typeof ValidationErrorResponseSchema>;

/**
 * Success with airline rules - Airline-specific modifications applied
 */
export const ValidationAirlineRulesResponseSchema = v.object({
  type: v.literal('airline_rules'),
  isValid: v.boolean(),
  airlineRules: v.array(AirlineRuleInfoSchema),
  traveller: AssociatedTravellerSchema,
});

export type ValidationAirlineRulesResponse = v.InferOutput<
  typeof ValidationAirlineRulesResponseSchema
>;

/**
 * Discriminated union of all possible validation response states
 * Use the 'type' field to narrow the response type
 */
export const ValidationResponseSchema = v.variant('type', [
  ValidationSuccessResponseSchema,
  ValidationErrorResponseSchema,
  ValidationAirlineRulesResponseSchema,
]);

export type ValidationResponse = v.InferOutput<typeof ValidationResponseSchema>;

// ============================================================================
// Validation Error Class
// ============================================================================

/**
 * Validation error thrown when traveller validation fails
 */
export class TravellerValidationError extends Error {
  constructor(
    message: string,
    public readonly errors?: TravellerFieldErrors,
    public readonly issues?: Array<{ path: string[]; message: string }>,
  ) {
    super(message);
    this.name = 'TravellerValidationError';
  }
}

export const ValidateTravellerFormRequestSchema = v.object({
  coreRequest: v.object({
    associatedTravellers: v.array(AssociatedTravellerSchema),
    contactDetails: v.object({
      rowFields: v.record(v.string(), v.string()),
    }),
    gstDetails: v.optional(
      v.object({
        rowFields: v.record(v.string(), v.string()),
        optIn: v.boolean(),
      }),
    ),
    travellerBillingAddressDetails: v.optional(
      v.object({
        state: v.string(),
        stateId: v.string(),
      }),
    ),
  }),
});

/**
 * Validate traveller form request
 */
export type ValidateTravellerFormRequest = v.InferOutput<typeof ValidateTravellerFormRequestSchema>;

export const ValidateTravellerFormRequestParamsSchema = v.object({
  crId: v.string(),
  itineraryId: v.string(),
  currency: v.optional(v.string()),
  language: v.optional(v.string()),
  region: v.optional(v.string()),
  cabinClass: v.optional(v.string()),
  requestBody: ValidateTravellerFormRequestSchema,
  sessionId: v.optional(v.string()),
  userId: v.optional(v.string()),
});

export type ValidateTravellerFormRequestParams = v.InferOutput<
  typeof ValidateTravellerFormRequestParamsSchema
>;

export const ValidateTravellerInfoRequestSchema = v.object({
  coreRequest: v.object({
    associatedTravellers: v.array(AssociatedTravellerSchema),
  }),
});

/**
 * Validate traveller info request
 */
export type ValidateTravellerInfoRequest = v.InferOutput<typeof ValidateTravellerInfoRequestSchema>;

export const ValidateTravellerInfoRequestParamsSchema = v.object({
  crId: v.string(),
  itineraryId: v.string(),
  sessionId: v.optional(v.string()),
  userId: v.optional(v.string()),
  requestBody: ValidateTravellerInfoRequestSchema,
});

export type ValidateTravellerInfoRequestParams = v.InferOutput<
  typeof ValidateTravellerInfoRequestParamsSchema
>;
